import { Link } from "react-router-dom";
import type { ContentItem } from "@/types/content";
import { typeLabel, formatDate } from "@/lib/content";
import Tag from "@/components/base/Tag";

interface ContentCardProps {
  item: ContentItem;
  variant?: "row" | "col" | "compact";
}

const typeVariant: Record<string, "accent" | "primary" | "secondary"> = {
  news: "accent",
  work: "primary",
  analysis: "secondary",
};

export default function ContentCard({ item, variant = "row" }: ContentCardProps) {
  const href = `/detail/${item.id}`;

  if (variant === "compact") {
    return (
      <Link
        to={href}
        className="group flex cursor-pointer gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
      >
        <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title}｜星空动漫`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {item.title}
          </h3>
          <div className="mt-2 flex items-center gap-2 text-xs text-foreground-500">
            <span>{formatDate(item.date)}</span>
            <span className="text-foreground-300">·</span>
            <span>{typeLabel(item.type)}</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "col") {
    return (
      <Link
        to={href}
        className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
      >
        <div className="aspect-[16/10] w-full overflow-hidden bg-background-200">
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title}｜星空动漫`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="flex flex-1 flex-col p-4">
          <div className="mb-2 flex items-center gap-2">
            <Tag variant={typeVariant[item.type] || "secondary"}>{typeLabel(item.type)}</Tag>
            <Tag variant="secondary">{item.category}</Tag>
          </div>
          <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {item.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {item.summary}
          </p>
          <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
            <span>{item.author}</span>
            <span>{formatDate(item.date)}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={href}
      className="group flex cursor-pointer gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:p-4"
    >
      <div className="h-24 w-36 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-28 md:w-44">
        <img
          src={item.cover}
          alt={item.title}
          title={`${item.title}｜星空动漫`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
          {item.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {item.summary}
        </p>
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-3 text-xs text-foreground-500">
          <Tag variant={typeVariant[item.type] || "secondary"}>{item.category}</Tag>
          <span>{formatDate(item.date)}</span>
        </div>
      </div>
    </Link>
  );
}